import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { almacen } from '../api';
import { bajaDelLote } from './Lotes';
import { AsignarSerie } from './AsignarSerie';
import { Bienvenida } from './Bienvenida';
import {
  Cabecera,
  Cargando,
  Cifra,
  Fallo,
  Pastilla,
  PuntoDeNivel,
  Tarjeta,
  Vacio,
} from '../componentes';
import { NIVEL, cantidad, soles, solesRedondos } from '../formato';
import { comoRegistrarElStock, estadoDelStock } from '../estadoDelStock';
import { mayuscula, useNegocio, useVocabulario } from '../vocabulario';

/**
 * La portada: qué pide acción hoy.
 *
 * Cinco cifras y dos listas. No es un panel de indicadores para mirar de vez en
 * cuando: es lo que decide si alguien sale a comprar esta tarde o si hay que
 * tirar algo antes de que se pudra.
 */
export function Resumen() {
  const vocabulario = useVocabulario();
  const negocio = useNegocio();
  const resumen = useQuery({ queryKey: ['resumen'], queryFn: almacen.summary });
  const insumos = useQuery({ queryKey: ['insumos'], queryFn: almacen.supplies });
  const lotes = useQuery({ queryKey: ['lotes'], queryFn: () => almacen.lots() });
  const avisos = useQuery({ queryKey: ['avisos'], queryFn: almacen.alerts });
  const clientes = useQueryClient();
  // Un aviso de serie se cierra a mano, después de mirar el estante: no hay
  // cifra que vuelva sola a estar bien.
  const revisado = useMutation({
    mutationFn: (alertId: string) => almacen.resolveSerialAlert(alertId),
    onSuccess: () => void clientes.invalidateQueries({ queryKey: ['avisos'] }),
  });
  // «Asignar serie» del aviso abierto, si hay uno.
  const [asignando, setAsignando] = useState<{ id: string; producto: string } | null>(null);

  if (resumen.isError) {
    return (
      <>
        <Cabecera titulo="Almacén" subtitulo="Valorización, alertas y reposición del local" ayuda="La portada: lo que pide acción hoy. Qué falta comprar, qué está por vencer y cuánto vale el almacén." />
        <div className="cuerpo">
          <Fallo error={resumen.error} onReintentar={() => resumen.refetch()} />
        </div>
      </>
    );
  }

  if (resumen.data === undefined) {
    return (
      <>
        <Cabecera titulo="Almacén" subtitulo="Valorización, alertas y reposición del local" ayuda="La portada: lo que pide acción hoy. Qué falta comprar, qué está por vencer y cuánto vale el almacén." />
        <div className="cuerpo">
          <Cargando que="el almacén" />
        </div>
      </>
    );
  }

  const datos = resumen.data;

  /*
   * Lo que hay que reponer, ordenado por lo urgente que es, o que todavía no
   * se registró nada (`estadoDelStock`).
   *
   * Por nivel y no por importe: la lista existe para decidir a qué se sale
   * ahora, y el insumo más caro no es el que deja al local sin poder vender.
   * Solo lo que tiene mínimo: el 8 de octubre una tienda de ropa recién
   * abierta salía «41 de 41 en nivel crítico» con «pedir 0 unid» en cada fila.
   */
  const estado = estadoDelStock(datos.stockRecorded, insumos.data ?? []);
  const sinRegistrar = estado.tipo === 'sin-registrar';
  const aReponer = estado.tipo === 'registrado' ? estado.aReponer.slice(0, 6) : [];
  const vendidoDeMas = estado.tipo === 'registrado' ? estado.vendidoDeMas : [];
  const agotadosSinMinimo = estado.tipo === 'registrado' ? estado.agotadosSinMinimo : [];

  const enRiesgo = (lotes.data ?? [])
    .filter((lote) => lote.expiry !== 'fresh')
    .slice(0, 6);

  return (
    <>
      <Cabecera
        titulo="Almacén"
        subtitulo="Valorización, alertas y reposición del local" ayuda="La portada: lo que pide acción hoy. Qué falta comprar, qué está por vencer y cuánto vale el almacén."
      />

      <div className="cuerpo">
        <Bienvenida />

        {/*
         * Los avisos van arriba de todo y con **desde cuándo**.
         *
         * Que algo esté bajo mínimo ya se ve en la lista de abajo. Lo que solo
         * está aquí es hace cuánto: si el pulpo falta desde ayer o desde hace
         * tres semanas es la diferencia entre un descuido y un proveedor que
         * dejó de venir, y eso no se puede mirar en ningún otro sitio.
         */}
        {(avisos.data ?? []).length > 0 ? (
          <Tarjeta>
            <h2 className="tarjeta__titulo">
              Avisos abiertos{' '}
              <span className="mono apagado">({avisos.data?.length})</span>
            </h2>
            <p className="nota">
              Esto también sale por correo a quien compra, una sola vez por cada
              cosa que empieza a faltar: quien tiene que enterarse está en el
              mercado, no delante de esta pantalla.
            </p>
            <ul className="lista">
              {(avisos.data ?? []).map((aviso) => (
                <li key={aviso.id} className="lista__fila">
                  <div className="lista__texto">
                    <strong>{aviso.kind === 'model_below_minimum' ? 'Modelo bajo su mínimo' : aviso.supply}</strong>
                    <span className="apagado">
                      {aviso.kind === 'below_minimum' && Number(aviso.threshold) === 0
                        ? // «Avisar al llegar a cero», sin mínimo (migración 274).
                          `Se acabó: quedan ${cantidad(aviso.quantity)} ${aviso.unit}`
                        : aviso.kind === 'model_below_minimum'
                          ? // La nota lleva el modelo y sus versiones; el insumo es solo el que lo disparó.
                            (aviso.note ?? `Quedan ${cantidad(aviso.quantity)} de un mínimo de ${cantidad(aviso.threshold)}`)
                        : aviso.kind === 'below_minimum'
                        ? `Quedan ${cantidad(aviso.quantity)} ${aviso.unit} de un mínimo de ${cantidad(aviso.threshold)}`
                        : aviso.kind === 'expired_withheld'
                          ? // El lote en la venta: lo vencido no salió, la venta sí.
                            `Se vendieron ${cantidad(aviso.quantity)} ${aviso.unit} cuando solo quedaba vencido: revisa el estante`
                          : aviso.kind === 'serial_conflict' || aviso.kind === 'serial_missing'
                            ? // La venta salió igual, sin lote: alguien mira qué equipo se entregó.
                              (aviso.note ?? 'Una venta por serie que hay que revisar')
                            : `Lote ${aviso.lot ?? '—'} por vencer · ${cantidad(aviso.quantity)} ${aviso.unit}`}
                    </span>
                  </div>
                  {aviso.kind === 'serial_conflict' || aviso.kind === 'serial_missing' ? (
                    <>
                      <button
                        type="button"
                        className="accion-fila"
                        onClick={() => setAsignando({ id: aviso.id, producto: aviso.supply })}
                      >
                        Asignar serie
                      </button>
                      <button
                        type="button"
                        className="accion-fila"
                        disabled={revisado.isPending}
                        onClick={() => revisado.mutate(aviso.id)}
                      >
                        Revisado
                      </button>
                    </>
                  ) : null}
                  <Pastilla
                    texto={desdeCuando(aviso.openedAt)}
                    tono={aviso.kind === 'below_minimum' || aviso.kind === 'model_below_minimum' ? 'aviso' : 'peligro'}
                  />
                </li>
              ))}
            </ul>
            {revisado.isError ? <Fallo error={revisado.error} /> : null}
            {asignando !== null ? (
              <AsignarSerie
                alertId={asignando.id}
                producto={asignando.producto}
                onCerrar={() => setAsignando(null)}
              />
            ) : null}
          </Tarjeta>
        ) : null}
        <div className="cifras">
          <Cifra
            rotulo="Valorizado"
            ayuda="Lo que vale todo lo que hay en el almacén ahora: cantidad por costo promedio, sin IGV."
            valor={solesRedondos(datos.totalValue)}
            apoyo={`${datos.activeSupplies} ${vocabulario.insumos} activos`}
          />
          <Cifra
            rotulo="Bajo mínimo"
            ayuda={`${mayuscula(vocabulario.insumos)} que bajaron del mínimo que les pusiste. Sin mínimo no cuentan. «Crítico» es que queda muy poco o nada.`}
            valor={sinRegistrar ? '—' : String(datos.belowMinimum)}
            apoyo={
              sinRegistrar
                ? 'todavía sin registrar'
                : datos.critical === 1
                  ? '1 en nivel crítico'
                  : `${datos.critical} en nivel crítico`
            }
            tono={!sinRegistrar && datos.belowMinimum > 0 ? 'aviso' : 'neutro'}
          />
          <Cifra
            rotulo="Por vencer"
            ayuda="Lotes que vencen en los próximos días. Conviene usarlos primero."
            valor={String(datos.expiringSoon)}
            apoyo="en los próximos días"
            tono={datos.expiringSoon > 0 ? 'aviso' : 'neutro'}
          />
          <Cifra
            rotulo="Vencido sin baja"
            ayuda="Lotes que ya vencieron y siguen en el almacén. Hay que registrarlos como merma."
            valor={String(datos.expired)}
            apoyo={
              datos.expired > 0
                ? 'requiere merma inmediata'
                : 'nada esperando de baja'
            }
            tono={datos.expired > 0 ? 'peligro' : 'neutro'}
          />
          <Cifra
            rotulo="Valor en riesgo"
            ayuda="Lo que vale lo que está por vencer o vencido: se pierde si no se usa a tiempo."
            valor={solesRedondos(datos.valueAtRisk)}
            apoyo="si no se consume a tiempo"
          />
        </div>

        <div className="dos-columnas">
          {estado.tipo === 'sin-registrar' ? (
            /*
             * Una sola tarjeta y no un muro de filas «crítico»: en un local que
             * nunca registró lo que tiene, cero no dice «se acabó» sino «no se
             * contó». La bodega que solo vende no tiene nada que comprar según
             * el sistema; tiene que contar primero.
             */
            <Tarjeta>
              <h2 className="tarjeta__titulo">Todavía no registraste lo que tienes</h2>
              <p className="nota">
                Mientras no cargues tu stock, el almacén no puede decirte qué
                falta ni qué comprar: todo lo que vendes sale de cero.
                {estado.vendidoDeMas > 0
                  ? ` Ya vendiste ${estado.vendidoDeMas === 1 ? '1 ' + vocabulario.insumo : `${estado.vendidoDeMas} ${vocabulario.insumos}`} sin haberlos registrado.`
                  : ''}
              </p>
              <div className="acciones">
                {comoRegistrarElStock(negocio).map((paso, indice) => (
                  <a
                    key={paso.href}
                    className={indice === 0 ? 'boton boton--primario' : 'boton'}
                    href={paso.href}
                  >
                    {paso.texto}
                  </a>
                ))}
              </div>
            </Tarjeta>
          ) : (
          <Tarjeta>
            <h2 className="tarjeta__titulo">Reposición sugerida</h2>

            {aReponer.length === 0 && agotadosSinMinimo.length === 0 ? (
              <Vacio
                titulo="Nada por reponer"
                mensaje={`Ningún ${vocabulario.insumo} con mínimo está por debajo de él. Cuando alguno baje, aparece aquí con cuánto pedir y a quién. Lo que no tiene mínimo no se vigila.`}
              />
            ) : (
              <ul className="lista">
                {aReponer.map((insumo) => (
                  <li key={insumo.id} className="lista__fila">
                    <PuntoDeNivel tono={NIVEL[insumo.level].tono} />
                    <div className="lista__texto">
                      <strong>{insumo.name}</strong>
                      <span className="apagado">
                        Tiene {cantidad(insumo.quantity)} {insumo.unit} · mínimo{' '}
                        {cantidad(insumo.minimum)}
                        {insumo.supplier === null ? '' : ` · ${insumo.supplier}`}
                      </span>
                    </div>
                    <span className="mono">
                      {cantidad(insumo.suggestedRestock)} {insumo.unit}
                    </span>
                  </li>
                ))}
                {/*
                 * Lo que se acabó sin mínimo, al final y aparte: no hay cuánto
                 * pedir —sin mínimo no se sabe—, pero quien compra tiene que
                 * enterarse de que no queda. Ponerle un mínimo lo pasa arriba.
                 */}
                {agotadosSinMinimo.slice(0, 6).map((insumo) => (
                  <li key={insumo.id} className="lista__fila">
                    <PuntoDeNivel tono={NIVEL.out.tono} />
                    <div className="lista__texto">
                      <strong>{insumo.name}</strong>
                      <span className="apagado">
                        Se acabó · sin mínimo
                        {insumo.supplier === null ? '' : ` · ${insumo.supplier}`}
                      </span>
                    </div>
                    <span className="mono apagado">—</span>
                  </li>
                ))}
              </ul>
            )}
          </Tarjeta>
          )}

          <Tarjeta>
            <h2 className="tarjeta__titulo">Requiere acción hoy</h2>

            {enRiesgo.length === 0 ? (
              <Vacio
                titulo="Nada urgente"
                mensaje="Ningún lote está vencido ni a punto de vencer. Aquí aparece lo que hay que usar antes de que se eche a perder."
              />
            ) : (
              <ul className="lista">
                {enRiesgo.map((lote) => (
                  <li key={lote.id} className="lista__fila">
                    <div className="lista__texto">
                      <strong>{lote.supply}</strong>
                      <span className="apagado">
                        Lote {lote.code} · {cantidad(lote.quantity)} {lote.unit} ·{' '}
                        {soles(lote.value)}
                      </span>
                    </div>
                    <Pastilla
                      texto={lote.expiryLabel}
                      tono={lote.expiry === 'expired' ? 'peligro' : 'aviso'}
                    />
                    <a className="enlace-sutil" href={bajaDelLote(lote.id)}>
                      Dar de baja
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Tarjeta>
        </div>

        {/*
         * Lo vendido de más: el saldo quedó en negativo. No se esconde —el
         * libro manda (docs/15 §1) y vender sin stock está permitido a
         * propósito—, pero tampoco es una compra: comprar no arregla el
         * número, lo tapa. Falta registrar una entrada o contar.
         */}
        {vendidoDeMas.length > 0 ? (
          <Tarjeta>
            <h2 className="tarjeta__titulo">
              Vendiste más de lo que registraste{' '}
              <span className="mono apagado">({vendidoDeMas.length})</span>
            </h2>
            <p className="nota">
              El sistema dice que quedan menos de cero. Casi siempre falta
              registrar lo que llegó, o contar el estante.
            </p>
            <ul className="lista">
              {vendidoDeMas.slice(0, 6).map((insumo) => (
                <li key={insumo.id} className="lista__fila">
                  <div className="lista__texto">
                    <strong>{insumo.name}</strong>
                  </div>
                  <span className="mono">
                    {cantidad(insumo.quantity)} {insumo.unit}
                  </span>
                </li>
              ))}
            </ul>
            <div className="acciones">
              <a className="boton" href="#/recepcion">Registrar lo que llegó</a>
              <a className="boton" href="#/conteo">Contar el estante</a>
            </div>
          </Tarjeta>
        ) : null}
      </div>
    </>
  );
}

/**
 * «Desde ayer», «hace 3 días».
 *
 * En días y no con la fecha exacta porque la pregunta no es cuándo pasó sino
 * cuánto lleva pasando: «31 de agosto» obliga a restar de cabeza y «hace 4
 * días» ya es la respuesta.
 */
function desdeCuando(iso: string): string {
  const dias = Math.floor(
    (Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000),
  );

  if (dias <= 0) return 'Hoy';
  if (dias === 1) return 'Desde ayer';

  return `Hace ${dias} días`;
}
